import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function SeedModal({ isOpen, onClose, onSave, caballonIndex, caballonData, longitudTotal, availableSeeds, defaultSide = 'left' }) {
  const [selectedSeed, setSelectedSeed] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');
  const [side, setSide] = useState(defaultSide);
  const [variety, setVariety] = useState('');

  const currentSideData = caballonData.filter(i => (i.side || 'left') === side);

  // Calculate used space EXCLUDING the currently selected seed (and variety) if it was already planted
  const getAvailableSpaceForSeed = (seed, varStr = '') => {
    if (!seed) return 0;
    const usedSpaceByOthers = currentSideData
      .filter(item => !(item.seed.id === seed.id && (item.variety || '') === varStr))
      .reduce((acc, item) => acc + (item.quantity * item.seed.distanceCm), 0);
    return longitudTotal - usedSpaceByOthers;
  };

  const getMaxQuantity = (seed, varStr = '') => {
    if (!seed) return 0;
    const available = getAvailableSpaceForSeed(seed, varStr);
    return Math.floor(available / seed.distanceCm);
  };

  useEffect(() => {
    if (isOpen) {
      setSelectedSeed(null);
      setQuantity('');
      setDate(new Date().toISOString().split('T')[0]);
      setSearchTerm('');
      setSide(defaultSide);
      setVariety('');
    }
  }, [isOpen, defaultSide]);

  // When seed changes, check if quantity exceeds new max, or initialize it
  useEffect(() => {
    if (selectedSeed) {
      // Find if we already have this seed planted on this side with this variety
      const existingItem = currentSideData.find(i => i.seed.id === selectedSeed.id && (i.variety || '') === variety);
      
      if (existingItem && existingItem.quantity > 0) {
         setQuantity(existingItem.quantity); // preload existing quantity
         if (existingItem.date) setDate(existingItem.date);
      } else {
         setQuantity('');
      }
    }
  }, [selectedSeed, side, variety]);

  if (!isOpen) return null;

  const maxQuantity = selectedSeed ? getMaxQuantity(selectedSeed, variety) : 0;
  
  // Calculate currently available space
  const totalUsedSpace = currentSideData.reduce((acc, item) => acc + (item.quantity * item.seed.distanceCm), 0);
  const availableSpaceGeneral = longitudTotal - totalUsedSpace;

  const handleSave = () => {
    if (!selectedSeed) return;
    const parsedQty = quantity === '' ? 0 : (parseInt(quantity) || 0);
    if (parsedQty < 0) return;
    onSave(caballonIndex, {
      seed: selectedSeed,
      quantity: parsedQty,
      date,
      side,
      variety
    });
  };

  const filteredSeeds = availableSeeds.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

  return createPortal(
    <div className="modal-overlay" onClick={(e) => {
      if(e.target.className === 'modal-overlay') onClose();
    }}>
      <div className="modal-content" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>
            Plantar en Caballón {caballonIndex + 1}
          </h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="flex-between" style={{ marginBottom: '16px' }}>
          <div className="side-toggle">
            <button 
              className={`side-btn ${side === 'left' ? 'active' : ''}`}
              onClick={() => { setSide('left'); setSelectedSeed(null); }}
            >
              Lado Izquierdo
            </button>
            <button 
              className={`side-btn ${side === 'right' ? 'active' : ''}`}
              onClick={() => { setSide('right'); setSelectedSeed(null); }}
            >
              Lado Derecho
            </button>
          </div>
        </div>

        <div className="flex-between" style={{ marginBottom: '12px' }}>
          <p style={{ fontSize: '14px', margin: 0 }}>
            Espacio libre en este lado: <strong>{availableSpaceGeneral} cm</strong>
          </p>
        </div>

        <div className="input-group mb-4" style={{ marginBottom: '12px' }}>
          <input 
            type="text" 
            placeholder="🔍 Buscar semilla..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ padding: '8px 12px', fontSize: '14px' }}
          />
        </div>

        <div className="seeds-list-scroll">
          <div className="seeds-grid">
            {filteredSeeds.map((seed) => {
              const maxPossible = Math.floor(getAvailableSpaceForSeed(seed) / seed.distanceCm);
              const isPlanted = currentSideData.find(i => i.seed.id === seed.id);

              return (
                <div 
                  key={seed.id}
                  className={`seed-option ${selectedSeed?.id === seed.id ? 'selected' : ''}`}
                  onClick={() => setSelectedSeed(seed)}
                >
                  <div className="seed-icon">{seed.icon}</div>
                  <div className="seed-name">{seed.name}</div>
                  <div className="seed-dist">{seed.distanceCm}cm/ud</div>
                  {isPlanted && <div className="seed-badge">✔</div>}
                </div>
              );
            })}
          </div>
        </div>

        {selectedSeed && (
          <>
            {selectedSeed.varieties && selectedSeed.varieties.length > 0 && (
              <div className="input-group animate-scale mt-4">
                <label htmlFor="variety">Variedad</label>
                <select
                  id="variety"
                  value={variety}
                  onChange={(e) => setVariety(e.target.value)}
                >
                  <option value="">--- Sin variedad específica ---</option>
                  {selectedSeed.varieties.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="input-group animate-scale mt-4">
              <label htmlFor="quantity">
                Cantidad de {selectedSeed.name.toLowerCase()}s
                <span style={{ float: 'right', fontWeight: 'normal', fontSize: '12px', color: 'var(--text-secondary)' }}>
                  Máx: {maxQuantity}
                </span>
              </label>
              <input 
                type="number" 
                id="quantity" 
                min="0" 
                value={quantity} 
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '') {
                    setQuantity('');
                  } else {
                    setQuantity(Math.max(0, parseInt(val) || 0));
                  }
                }} 
              />
              <small style={{ color: (Number(quantity) || 0) > maxQuantity ? 'var(--danger-color)' : 'var(--text-secondary)', display: 'block', marginTop: '4px', fontWeight: (Number(quantity) || 0) > maxQuantity ? 600 : 'normal' }}>
                Ocupará: {(Number(quantity) || 0) * selectedSeed.distanceCm} cm de los {getAvailableSpaceForSeed(selectedSeed, variety)} cm disponibles.
                {(Number(quantity) || 0) > maxQuantity && " (¡Límite sobrepasado!)"}
              </small>
            </div>
            
            <div className="input-group animate-scale">
              <label htmlFor="plantDate">Fecha de plantación</label>
              <input 
                type="date" 
                id="plantDate" 
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button 
            className="btn" 
            onClick={handleSave} 
            disabled={!selectedSeed}
            style={{ flex: 1, opacity: !selectedSeed ? 0.5 : 1 }}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
